// src/components/dayClose/AnalyzingProgress.tsx
// AI 分析進度指示器 - 水玻璃風格

import React, { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, CheckCircle } from 'lucide-react';

interface AnalyzingProgressProps {
  stage: 'analyzing' | 'calculating' | 'finalizing' | 'complete';
}

interface Stage {
  key: string;
  icon: React.ElementType;
  label: string;
  description: string;
}

const STAGES: Stage[] = [
  {
    key: 'analyzing',
    icon: Sparkles,
    label: 'Analyzing Activities',
    description: 'Understanding your contributions...'
  },
  {
    key: 'calculating',
    icon: TrendingUp,
    label: 'Calculating Ripples',
    description: 'Computing impact across goals...'
  },
  {
    key: 'finalizing',
    icon: CheckCircle,
    label: 'Finalizing',
    description: 'Preparing your insights...'
  }
];

// 動態訊息組件 - 降低等待焦慮
const CALCULATING_MESSAGES = [
  'Analyzing activity patterns',
  'Matching goals and contributions',
  'Calculating life dimension impact',
  'Measuring your progress',
  'Evaluating long-term effects',
  'Connecting the dots',
  'Computing your ripples',
  'Almost there'
];

const RotatingMessage: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % CALCULATING_MESSAGES.length);
    }, 1500); // 每 1.5 秒切換

    return () => clearInterval(interval);
  }, []);

  return (
    <span className="transition-opacity duration-300">
      {CALCULATING_MESSAGES[currentIndex]}...
    </span>
  );
};

export const AnalyzingProgress: React.FC<AnalyzingProgressProps> = ({ stage }) => {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);

  useEffect(() => {
    const stageIndex = STAGES.findIndex(s => s.key === stage);
    if (stageIndex !== -1) {
      setCurrentStageIndex(stageIndex);
    }
  }, [stage]);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {/* 主要動畫圖示 - 水玻璃效果 */}
      <div className="relative mb-6">
        {/* 外圈脈衝動畫 - 更微妙的效果 */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 bg-sky-200/10 rounded-full animate-ping opacity-50"></div>
        </div>

        {/* 中心圖示 - 水玻璃效果 */}
        <div className="relative w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/40 shadow-lg shadow-sky-500/20">
          {React.createElement(STAGES[currentStageIndex].icon, {
            className: 'w-8 h-8 text-sky-500 animate-breathing'
          })}
        </div>
      </div>

      {/* 當前階段文字 */}
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {STAGES[currentStageIndex].label}
        </h3>
        <p className="text-sm text-gray-600">
          {stage === 'calculating' ? (
            <RotatingMessage />
          ) : (
            STAGES[currentStageIndex].description
          )}
        </p>
      </div>

      {/* 階段指示器 */}
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-2">
          {STAGES.map((stageItem, index) => {
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            const Icon = stageItem.icon;

            return (
              <div key={stageItem.key} className="flex flex-col items-center flex-1">
                {/* 圖示 - 水玻璃效果 */}
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-300 backdrop-blur-sm
                    ${isCompleted
                      ? 'bg-sky-500/80 text-white scale-100'
                      : isCurrent
                      ? 'bg-sky-400/30 text-sky-500 scale-110 shadow-lg border border-white/40'
                      : 'bg-gray-300/40 text-gray-400 scale-90'
                    }
                  `}
                >
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <Icon className={`w-5 h-5 ${isCurrent ? 'animate-pulse' : ''}`} />
                  )}
                </div>

                {/* 標籤（僅在小螢幕隱藏）*/}
                <span
                  className={`
                    text-xs text-center hidden sm:block
                    ${isCompleted || isCurrent ? 'text-gray-900 font-medium' : 'text-gray-400'}
                  `}
                >
                  {stageItem.label.split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>

        {/* 進度百分比條 - 水玻璃效果 */}
        <div className="w-full bg-gray-200/40 rounded-full h-2 overflow-hidden mt-6 backdrop-blur-sm">
          <div
            className="h-full bg-gradient-to-r from-sky-400/60 to-sky-500/60 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${((currentStageIndex + 1) / STAGES.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* 提示文字 */}
      <p className="text-xs text-gray-500 mt-4 text-center">
        This may take a few moments...
      </p>
    </div>
  );
};
